import type { HotEligibleResourceType } from '@ctw/shared/utils/fhir/resource-mappings';
import { startCase } from 'lodash-es';

/**
 * Singular, human-readable noun for each hot-eligible FHIR resource type, for building
 * natural-language copy (e.g. "2 documents and 1 diagnostic report"). `DiagnosticReport` and
 * `Observation` match the labels `diagnosticReportDrawerAction`/`observationDrawerAction` already
 * show users. `Binary` and `DocumentReference` are both labeled "Document" elsewhere in the
 * product (`binaryDrawerAction`, `documentReferenceDrawerAction`) since a `Binary` is a
 * `DocumentReference`'s underlying file content — but a sentence listing both types' counts needs
 * each to read as a distinct noun, so `Binary` uses its literal FHIR resource name, "binary",
 * here instead of a friendlier synonym that would obscure which record is which.
 */
export const HOT_ELIGIBLE_RESOURCE_TYPE_DISPLAY_NAMES: Record<HotEligibleResourceType, string> = {
  Binary: 'binary',
  CareTeam: 'care team',
  DocumentReference: 'document',
  DiagnosticReport: 'diagnostic report',
  Observation: 'observation',
  Encounter: 'encounter',
  EpisodeOfCare: 'episode of care',
  Condition: 'condition',
  AllergyIntolerance: 'allergy',
  Immunization: 'immunization',
  MedicationStatement: 'medication',
  MedicationAdministration: 'medication administration',
  MedicationDispense: 'medication dispense',
  MedicationRequest: 'medication request',
  Coverage: 'coverage',
};

/**
 * Returns the singular, human-readable noun for resourceType. Looks up
 * `HOT_ELIGIBLE_RESOURCE_TYPE_DISPLAY_NAMES` by own property only — `Object.hasOwn` rather than a
 * plain index — so a prototype member's name (`toString`, `valueOf`, `constructor`,
 * `hasOwnProperty`, ...) can't resolve to the inherited function instead of falling through to the
 * fallback below.
 *
 * A type not in the map falls back to a lowercased, space-separated rendering of the raw FHIR
 * resource type (e.g. `ServiceRequest` -> `service request`), or `'resource'` when that
 * fallback itself would be empty (an empty `resourceType`), so the noun is always a non-empty
 * string.
 */
export const getResourceTypeDisplayName = (resourceType: string): string => {
  if (Object.hasOwn(HOT_ELIGIBLE_RESOURCE_TYPE_DISPLAY_NAMES, resourceType)) {
    return HOT_ELIGIBLE_RESOURCE_TYPE_DISPLAY_NAMES[resourceType as HotEligibleResourceType];
  }

  const fallbackDisplayName = startCase(resourceType).toLowerCase();
  return fallbackDisplayName === '' ? 'resource' : fallbackDisplayName;
};
